import { contactContent } from '@/mocks/homeData';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function ContactSection() {
  const { ref: leftRef, isVisible: leftVisible } = useScrollReveal(0.1);
  const { ref: rightRef, isVisible: rightVisible } = useScrollReveal(0.1);

  return (
    <section
      id="contact"
      className="relative py-20 md:py-28 bg-background-50"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 xl:px-16">
        <div className="max-w-6xl mx-auto">
          {/* 2컬럼 레이아웃 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-12 items-start">
            {/* 왼쪽 — 연락처 정보 */}
            <div
              ref={leftRef}
              className={`transition-all duration-700 ${
                leftVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <span className="inline-block text-xs font-medium tracking-[0.2em] text-foreground-500 uppercase mb-4">
                Consultation
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 leading-tight">
                이혼전문변호사와의
                <br />
                <span className="text-primary-600">1:1 맞춤 상담</span>
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-md">
                {contactContent.sectionSubtitle}
              </p>

              <div className="mt-8 md:mt-10 space-y-5">
                {/* 휴대폰 */}
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-500 text-background-50 shrink-0">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-phone-line text-lg" />
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">휴대폰</p>
                    <a
                      href={`tel:${contactContent.phone.number}`}
                      className="text-sm md:text-base font-semibold text-foreground-950 hover:text-primary-600 transition-colors"
                    >
                      {contactContent.phone.display}
                    </a>
                  </div>
                </div>

                {/* 주소 */}
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-500 text-background-50 shrink-0">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-map-pin-line text-lg" />
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">주소</p>
                    <p className="text-sm md:text-base font-semibold text-foreground-950 leading-snug">
                      {contactContent.address.full}
                    </p>
                  </div>
                </div>

                {/* 운영 시간 */}
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-500 text-background-50 shrink-0">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-time-line text-lg" />
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">운영 시간</p>
                    <p className="text-sm md:text-base font-semibold text-foreground-950">
                      평일 09:00 - 18:00
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 오른쪽 — 예약 카드 */}
            <div
              ref={rightRef}
              className={`transition-all duration-700 delay-150 ${
                rightVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <div className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden">
                {/* 상단 강조 라인 */}
                <div className="h-1.5 bg-primary-500" />

                <div className="p-6 md:p-8">
                  {/* 네이버 아이콘 + 제목 */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#03C75A] text-white font-bold text-lg shrink-0">
                      N
                    </div>
                    <div>
                      <p className="text-base font-bold text-foreground-950">
                        {contactContent.reservation.title}
                      </p>
                      <p className="text-xs text-foreground-500">
                        {contactContent.reservation.subtitle}
                      </p>
                    </div>
                  </div>

                  {/* 안내 텍스트 박스 */}
                  <div className="p-4 rounded-xl bg-background-100 text-sm text-foreground-600 leading-relaxed mb-5">
                    {contactContent.reservation.guide}
                  </div>

                  {/* 네이버 예약 버튼 */}
                  <a
                    href={contactContent.naverMap.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-4 rounded-xl text-base font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <span className="w-5 h-5 flex items-center justify-center rounded bg-background-50 text-primary-600 text-xs font-bold">
                      N
                    </span>
                    {contactContent.reservation.ctaText}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line" />
                    </span>
                  </a>

                  {/* 구분선 */}
                  <div className="my-6 h-px bg-background-200/70" />

                  {/* 빠른 전화 상담 */}
                  <p className="text-xs text-foreground-500 mb-3">
                    {contactContent.reservation.quickCallLabel}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 shrink-0">
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-phone-line text-lg" />
                      </span>
                    </div>
                    <div>
                      <a
                        href={`tel:${contactContent.phone.number}`}
                        className="text-base font-bold text-primary-600 hover:text-primary-700 transition-colors"
                      >
                        {contactContent.phone.display}
                      </a>
                      <p className="text-xs text-foreground-500 mt-0.5">
                        {contactContent.reservation.quickCallNote}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 하단 — 지도 */}
          <div className="mt-12 md:mt-16">
            <div className="w-full rounded-2xl overflow-hidden border border-background-200/70 bg-background-200">
              <iframe
                title="법률사무소 동행 위치"
                src="https://maps.google.com/maps?q=37.296711,127.068676&z=18&ie=UTF8&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '320px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <p className="mt-3 text-center text-sm text-foreground-500">
              경기 용인시 수지구 광교중앙로 297 광교2차 푸르지오시티 D동 205호
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}